import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Check, CircleHelp, Download, Inbox, SquarePen, X } from "lucide-react";
import {
  TOOLBOX_DOWNLOAD_PAGE_PATH,
  isHelpLinkPath,
  type HelpGuide,
  type HelpGuideSummary,
} from "@ttc-spectree/toolbox-contract";
import { MarkdownRenderer } from "@/components/common/markdown-renderer";
import { useMe } from "@/hooks/queries/use-me";
import { useGroomingQueue } from "@/hooks/use-grooming-queue";
import {
  useHelpBridge,
  type HelpAnswerMessage,
  type HelpBridgeView,
  type HelpChatMessage,
} from "@/hooks/use-help-bridge";
import { useToolboxBridge } from "@/lib/toolbox/bridge";
import { HELP_INBOX_PAGE_PATH } from "@/lib/toolbox/help";
import { TOOLBOX_TEXT } from "@/lib/toolbox/type-scale";
import { cn } from "@/lib/utils";
import { useHelpPanelStore } from "@/stores/help-panel-store";
import { HelpAskBox } from "./help-ask-box";
import { helpActionHandlersFor } from "./help-confirm-action";
import { HelpContextLine } from "./help-context-line";
import { HelpAnswer, HelpPersonBubble } from "./help-message";
import { HelpNotReady, helpNotReadyCopy } from "./help-not-ready";
import { HelpOutOfDateNotice, HelpReadyNotice } from "./help-notice";
import { HelpSendToAaron, helpSentTime } from "./help-send-to-aaron";
import { HelpGuideList, HelpSuggestions } from "./help-suggestions";

/**
 * The SpecTree Toolbox Help panel (epic PI-SPEC-SW-E302, feature
 * PI-SPEC-SW-1557, ER-1655; Claude Design "Toolbox Help Mockups" frames H1,
 * H2, H3, H5 and H6, design `.hp`): a 408 px panel over the right of the
 * current Toolbox screen, so the person still sees what they ask about.
 *
 * Header "Help" / "Questions about SpecTree Toolbox" with New conversation
 * and Close (and a slot for the Admin Inbox button); the context line; the
 * out-of-date line; the conversation or the empty state with suggestions; the
 * ask box; and the footer that says answers use the person's own Claude plan
 * and Help never changes anything on its own. App-only: ToolboxFrame mounts
 * it, and ToolboxFrame only renders inside SpecTree Toolbox.
 */

export const HELP_PANEL_ID = "toolbox-help-panel";

const SETUP_PATH = "/toolbox/setup";

/** A "Sent to Aaron · <time>" line, shown after the first `afterCount` messages of the conversation. */
export interface HelpSentNote {
  afterCount: number;
  at: Date;
}

const FOOTER_READY =
  "Answers use your Claude plan. Help reads the Toolbox guide and how the Toolbox is built. It never changes anything on its own.";

const inboxButton =
  "inline-flex h-[26px] flex-none items-center gap-1.5 whitespace-nowrap rounded-lg border border-[#27272A] bg-[#1A1A1E] px-[9px] text-xs font-medium text-[#FAFAFA] transition-colors hover:bg-[#222226]";

const iconButton =
  "flex h-[30px] w-[30px] flex-none items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-white/[0.04] hover:text-[#FAFAFA]";

interface HelpPanelProps {
  view: HelpBridgeView;
  onClose: () => void;
  /** Extra header buttons (the Admin Help inbox, added by a later feature). */
  headerActions?: ReactNode;
  /** "Send to Aaron" (opens H4). Without it the button is not shown. */
  onSendToAaron?: ((answer: HelpAnswerMessage) => void) | undefined;
  /**
   * A screen that takes the place of the context line, the conversation and
   * the ask box while it is open (Send to Aaron, frame H4). The header stays.
   */
  replacement?: ReactNode;
  /** "Sent to Aaron · <time>" lines in the conversation. */
  sentNotes?: readonly HelpSentNote[];
}

/** The guide being read in the panel. */
type GuideState =
  | { kind: "none" }
  | { kind: "loading"; summary: HelpGuideSummary }
  | { kind: "open"; guide: HelpGuide }
  | { kind: "failed"; summary: HelpGuideSummary; message: string };

function UpdateNeeded({ appUpdate }: { appUpdate: boolean }) {
  return (
    <div
      className="flex flex-col gap-2.5 rounded-[10px] border border-[rgba(245,158,11,0.4)] bg-[#14110A] p-4"
      data-testid="help-update-needed"
    >
      <span className="font-display text-sm font-semibold leading-[1.3]">
        {appUpdate ? "Help needs a newer SpecTree Toolbox" : "Help needs a newer SpecTree"}
      </span>
      <span className="text-[13px] leading-[1.55] text-[#E4E4E7]">
        {appUpdate
          ? "This copy of SpecTree Toolbox does not have Help yet. Download the latest version, then close the Toolbox and run the installer. Your queue is saved."
          : "This SpecTree does not serve Help for this version of the Toolbox yet. Help comes back once SpecTree is updated."}
      </span>
      {appUpdate && (
        <div className="flex">
          <Link
            to={TOOLBOX_DOWNLOAD_PAGE_PATH}
            className="inline-flex h-[26px] items-center gap-1.5 whitespace-nowrap rounded-lg bg-[#E11837] px-2.5 text-xs font-semibold text-white hover:bg-[#C8102E]"
          >
            <Download className="h-[13px] w-[13px]" aria-hidden="true" />
            Get the latest version
          </Link>
        </div>
      )}
    </div>
  );
}

/** "Sent to Aaron · 2:14 PM" under the messages that were sent. */
function SentLine({ at }: { at: Date }) {
  return (
    <span
      className={cn("flex items-center gap-1.5 self-center text-xs", TOOLBOX_TEXT.faint)}
      data-testid="help-sent-line"
    >
      <Check className="h-3 w-3" aria-hidden="true" />
      Sent to Aaron · {helpSentTime(at)}
    </span>
  );
}

export function HelpPanel({
  view,
  onClose,
  headerActions,
  onSendToAaron,
  replacement,
  sentNotes = [],
}: HelpPanelProps) {
  const bridge = useToolboxBridge();
  const navigate = useNavigate();
  const queue = useGroomingQueue();
  const handlers = useMemo(
    () => helpActionHandlersFor(bridge, { supported: queue.supported, actions: queue.actions }),
    [bridge, queue.supported, queue.actions]
  );
  const [guide, setGuide] = useState<GuideState>({ kind: "none" });
  const bodyRef = useRef<HTMLDivElement>(null);

  const { availability, status, packInfo, messages } = view;
  const updateNeeded =
    availability === "app-update-needed" || availability === "spectree-update-needed";
  const notReady = availability === "not-ready" && status !== null;
  const notReadyCopy = notReady ? helpNotReadyCopy(status) : null;
  const suggestions = packInfo?.suggestions[view.screenKey] ?? [];

  // Keep the newest text in view while an answer streams.
  // It follows the stream only while the person is at the bottom: scrolling up to re-read stays put.
  const last = messages[messages.length - 1];
  const lastLength = last && "text" in last ? last.text.length : 0;
  const stickToBottomRef = useRef(true);
  const lastCountRef = useRef(0);
  useEffect(() => {
    const body = bodyRef.current;
    if (!body || typeof body.scrollTo !== "function") return;
    const newMessage = messages.length !== lastCountRef.current;
    lastCountRef.current = messages.length;
    if (newMessage || stickToBottomRef.current) body.scrollTo({ top: body.scrollHeight });
  }, [lastLength, messages.length, last?.id]);

  const guideRequestRef = useRef(0);
  const openGuide = async (summary: HelpGuideSummary) => {
    guideRequestRef.current += 1;
    const request = guideRequestRef.current;
    setGuide({ kind: "loading", summary });
    const result = await view.getGuide(summary.id);
    if (request !== guideRequestRef.current) return;
    setGuide(
      result.ok
        ? { kind: "open", guide: result.guide }
        : { kind: "failed", summary, message: result.message }
    );
  };

  const openSetup = () => {
    void navigate(SETUP_PATH);
  };

  const renderMessages = (list: readonly HelpChatMessage[]) =>
    list.map((message, index) => (
      <div key={message.id} className="contents">
        {message.role === "person" ? (
          <HelpPersonBubble message={message} />
        ) : (
          <HelpAnswer message={message} handlers={handlers} onSendToAaron={onSendToAaron} />
        )}
        {sentNotes
          .filter((note) => note.afterCount === index + 1)
          .map((note) => (
            <SentLine key={note.at.getTime()} at={note.at} />
          ))}
      </div>
    ));

  let askReason: string | undefined;
  if (notReadyCopy) askReason = notReadyCopy.askReason;
  else if (availability === "loading") askReason = "Getting Help ready…";

  const body = (() => {
    if (updateNeeded) return <UpdateNeeded appUpdate={availability === "app-update-needed"} />;
    if (guide.kind !== "none") {
      const title = guide.kind === "open" ? guide.guide.title : guide.summary.title;
      return (
        <div className="flex flex-col gap-3" data-testid="help-guide">
          <button
            type="button"
            onClick={() => {
              guideRequestRef.current += 1;
              setGuide({ kind: "none" });
            }}
            className="inline-flex items-center gap-1.5 self-start text-xs text-muted-foreground hover:text-[#FAFAFA]"
          >
            <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
            Back
          </button>
          <span className="font-display text-base font-semibold">{title}</span>
          {guide.kind === "loading" && (
            <span className={cn("text-[13px]", TOOLBOX_TEXT.faint)}>Opening the guide…</span>
          )}
          {guide.kind === "failed" && (
            <span role="alert" className="text-[13px] text-[#FBBF24]">
              {guide.message.trim() || "That guide could not be opened."}
            </span>
          )}
          {guide.kind === "open" && (
            <MarkdownRenderer
              content={guide.guide.markdown}
              allowLink={isHelpLinkPath}
              className="text-[13px] leading-[1.55] text-[#E4E4E7] [&_h1]:text-base [&_h2]:text-sm [&_h3]:text-[13px]"
            />
          )}
        </div>
      );
    }
    if (notReady) {
      return (
        <>
          <HelpNotReady status={status} bridge={bridge} onOpenSetup={openSetup} onSignedIn={view.recheckStatus} />
          <HelpGuideList
            guides={packInfo?.guides ?? []}
            heading="Meanwhile, from the Toolbox guide"
            onOpen={(summary) => void openGuide(summary)}
          />
          {messages.length > 0 && (
            <div className="flex flex-col gap-3.5">{renderMessages(messages)}</div>
          )}
        </>
      );
    }
    if (messages.length === 0) {
      return (
        <>
          <div className="flex flex-col gap-1.5 pb-0.5 pt-1.5">
            <span className="font-display text-base font-semibold leading-[1.3]">
              What would you like to know?
            </span>
            <span className="text-[13px] leading-[1.55] text-muted-foreground">
              Ask how something works, what a word means, or why the screen looks the way it does.
              Attach a screenshot if it helps.
            </span>
          </div>
          <HelpSuggestions
            questions={suggestions}
            disabled={availability !== "ready" || view.asking}
            onAsk={(question) => void view.ask(question)}
          />
        </>
      );
    }
    return renderMessages(messages);
  })();

  return (
    <aside
      id={HELP_PANEL_ID}
      aria-label="Help"
      className="absolute bottom-0 right-0 top-0 z-30 flex w-[408px] flex-col border-l border-[#27272A] bg-[#0E0E10] text-[13px] text-[#FAFAFA] shadow-[-24px_0_48px_rgba(0,0,0,0.5)]"
      data-testid="help-panel"
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          event.stopPropagation();
          onClose();
        }
      }}
    >
      <div className="flex flex-none items-center gap-2 border-b border-[#1F1F23] pb-2.5 pl-4 pr-2.5 pt-3">
        <CircleHelp className="h-[18px] w-[18px] flex-none text-[#D4D4D8]" aria-hidden="true" />
        <div className="flex min-w-0 flex-1 flex-col gap-px">
          <h2 className="font-display text-sm font-semibold leading-[1.3]">Help</h2>
          <span className={cn("text-[11px]", TOOLBOX_TEXT.faint)}>
            Questions about SpecTree Toolbox
          </span>
        </div>
        {headerActions}
        {!updateNeeded && replacement === undefined && (
          <button
            type="button"
            className={iconButton}
            title="New conversation"
            aria-label="New conversation"
            onClick={() => {
              guideRequestRef.current += 1;
              setGuide({ kind: "none" });
              view.newConversation();
            }}
            data-testid="help-new-conversation"
          >
            <SquarePen className="h-4 w-4" aria-hidden="true" />
          </button>
        )}
        <button
          type="button"
          className={iconButton}
          title="Close"
          aria-label="Close Help"
          onClick={onClose}
          data-testid="help-close"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
      {replacement}
      {replacement === undefined && !updateNeeded && !notReady && (
        <HelpContextLine screen={view.screenLabel} project={view.project} roles={view.roles} />
      )}
      {replacement === undefined && !updateNeeded && !notReady && view.readyNotice && (
        <HelpReadyNotice text={view.readyNotice} />
      )}
      {replacement === undefined && !updateNeeded && view.outOfDate && (
        <HelpOutOfDateNotice
          packVersion={view.outOfDate.packVersion}
          serverVersion={view.outOfDate.serverVersion}
        />
      )}
      <div
        ref={bodyRef}
        onScroll={(event) => {
          const el = event.currentTarget;
          stickToBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 48;
        }}
        // Kept mounted under Send to Aaron so each answer keeps its local "Did this help?" choice.
        className={cn(
          "flex min-h-0 flex-1 flex-col gap-3.5 overflow-y-auto px-4 py-3.5",
          replacement !== undefined && "hidden"
        )}
        aria-live="polite"
        data-testid="help-body"
      >
        {body}
      </div>
      {replacement === undefined && !updateNeeded && (
        <div className="flex flex-none flex-col gap-2 border-t border-[#1F1F23] px-3 pb-3 pt-2.5">
          <HelpAskBox
            draft={view.draft}
            onDraftChange={view.setDraft}
            attachments={view.attachments}
            attachError={view.attachError}
            asking={view.asking}
            disabledReason={askReason}
            onSend={() => void view.ask()}
            onStop={view.stop}
            onAddFiles={(files) => void view.addImageFiles(files)}
            onRemove={view.removeAttachment}
            onCaptureScreen={() => void view.captureScreen()}
          />
          <div
            className={cn("px-0.5 text-[11px] leading-[1.55]", TOOLBOX_TEXT.faint)}
            data-testid="help-footer"
          >
            {notReadyCopy ? notReadyCopy.footer : FOOTER_READY}
          </div>
        </div>
      )}
    </aside>
  );
}

/**
 * The Help panel in ToolboxFrame: holds the conversation for as long as the
 * frame lives (closing the panel keeps it; a new conversation or leaving the
 * Toolbox pages ends it) and renders the panel while it is open.
 *
 * Send to Aaron (frame H4, feature PI-SPEC-SW-1559): the link at the end of
 * every answer, and the button on a not-sure answer, open the confirm screen
 * in place of the conversation; Send stores one report, then the
 * conversation shows "Sent to Aaron · <time>". An `onSendToAaron` prop
 * replaces that screen (tests and embedders).
 *
 * Global Admins also get an Inbox button in the header (frame H7 notes): it
 * closes the panel and opens the Help inbox page. Nobody else sees it.
 */
export function HelpPanelHost({
  headerActions,
  onSendToAaron,
}: {
  headerActions?: ReactNode;
  onSendToAaron?: ((answer: HelpAnswerMessage) => void) | undefined;
}) {
  const open = useHelpPanelStore((state) => state.open);
  const close = useHelpPanelStore((state) => state.close);
  const view = useHelpBridge();
  const navigate = useNavigate();
  const { data: me } = useMe();
  const isGlobalAdmin = me?.isGlobalAdmin === true;
  const [sending, setSending] = useState(false);
  const [sentNotes, setSentNotes] = useState<HelpSentNote[]>([]);

  // A new conversation drops the sent lines and closes the confirm screen.
  const messageCount = view.messages.length;
  useEffect(() => {
    if (messageCount === 0) {
      setSending(false);
      setSentNotes([]);
    }
  }, [messageCount]);

  if (!open || view.availability === "browser") return null;
  return (
    <HelpPanel
      view={view}
      onClose={close}
      headerActions={
        <>
          {headerActions}
          {isGlobalAdmin && (
            <button
              type="button"
              className={inboxButton}
              title="Help inbox: what people sent"
              onClick={() => {
                close();
                void navigate(HELP_INBOX_PAGE_PATH);
              }}
              data-testid="help-inbox-button"
            >
              <Inbox className="h-[13px] w-[13px]" aria-hidden="true" />
              Inbox
            </button>
          )}
        </>
      }
      onSendToAaron={
        onSendToAaron ??
        (() => {
          setSending(true);
        })
      }
      sentNotes={sentNotes}
      replacement={
        sending && messageCount > 0 ? (
          <HelpSendToAaron
            messages={view.messages}
            senderName={me?.name}
            roles={view.roles}
            onCancel={() => {
              setSending(false);
            }}
            onSent={(at) => {
              setSentNotes((current) => [...current, { afterCount: messageCount, at }]);
              setSending(false);
            }}
          />
        ) : undefined
      }
    />
  );
}
